import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Toaster } from "@/components/ui/sonner";

// Eagerly loaded (critical path)
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import RoleGate from "@/components/auth/RoleGate";
import { ROUTE_ROLES } from "@/data/routeRoles";

const gated = (path, el) => (ROUTE_ROLES[path] ? <RoleGate roles={ROUTE_ROLES[path]}>{el}</RoleGate> : el);

// Lazy-loaded pages
const StudentLoginPage = React.lazy(() => import("@/pages/StudentLoginPage"));
const InstructorLoginPage = React.lazy(() => import("@/pages/InstructorLoginPage"));
const ForgotPasswordPage = React.lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("@/pages/ResetPasswordPage"));
const AdminDashboard = React.lazy(() => import("@/pages/AdminDashboard"));
const CompanySettings = React.lazy(() => import("@/pages/CompanySettings"));
const CompanyLocations = React.lazy(() => import("@/pages/CompanyLocations"));
const StaffDirectory = React.lazy(() => import("@/pages/StaffDirectory"));
const ComplianceDocs = React.lazy(() => import("@/pages/ComplianceDocs"));
const ComplianceReportsPage = React.lazy(() => import("@/pages/ComplianceReportsPage"));
const AuditLogs = React.lazy(() => import("@/pages/AuditLogs"));
const UserManagement = React.lazy(() => import("@/pages/UserManagement"));
const LinkageHealthPage = React.lazy(() => import("@/pages/LinkageHealthPage"));
const StudentsPage = React.lazy(() => import("@/pages/StudentsPage"));
const CoursesPage = React.lazy(() => import("@/pages/CoursesPage"));
const ExamsPage = React.lazy(() => import("@/pages/ExamsPage"));
const CertificationsPage = React.lazy(() => import("@/pages/CertificationsPage"));
const ClassesPage = React.lazy(() => import("@/pages/ClassesPage"));
const NotificationsPage = React.lazy(() => import("@/pages/NotificationsPage"));
const StudentProgress = React.lazy(() => import("@/pages/StudentProgress"));
const StudentPortalPage = React.lazy(() => import("@/pages/StudentPortalPage"));
const InstructorPortalPage = React.lazy(() => import("@/pages/InstructorPortalPage"));
const AircraftPage = React.lazy(() => import("@/pages/AircraftPage"));
const FlightLogsPage = React.lazy(() => import("@/pages/FlightLogsPage"));
const MaintenancePage = React.lazy(() => import("@/pages/MaintenancePage"));
const SafetyPage = React.lazy(() => import("@/pages/SafetyPage"));
const WeatherPage = React.lazy(() => import("@/pages/WeatherPage"));
const BillingPage = React.lazy(() => import("@/pages/BillingPage"));
const PayrollPage = React.lazy(() => import("@/pages/PayrollPage"));
const ProcurementPage = React.lazy(() => import("@/pages/ProcurementPage"));
const ReportsPage = React.lazy(() => import("@/pages/ReportsPage"));
const DocumentsPage = React.lazy(() => import("@/pages/DocumentsPage"));
const IntegrationSettingsPage = React.lazy(() => import("@/pages/IntegrationSettingsPage"));
const SchedulingPage = React.lazy(() => import("@/pages/SchedulingPage"));
const AnalyticsDashboardPage = React.lazy(() => import("@/pages/AnalyticsDashboardPage"));
const CertificateVerifyPage = React.lazy(() => import("@/pages/CertificateVerifyPage"));
const TrainingProgramsPage = React.lazy(() => import("@/pages/TrainingProgramsPage"));
const StudentProgressPage = React.lazy(() => import("@/pages/StudentProgressPage"));
const StudentProfilePage = React.lazy(() => import("@/pages/StudentProfilePage"));
const SkillTestsPage = React.lazy(() => import("@/pages/SkillTestsPage"));
const AviationReportsPage = React.lazy(() => import("@/pages/AviationReportsPage"));
const FeeCalculatorPage = React.lazy(() => import("@/pages/FeeCalculatorPage"));
const FinanceReportsPage = React.lazy(() => import("@/pages/FinanceReportsPage"));
const PaymentPlansPage = React.lazy(() => import("@/pages/PaymentPlansPage"));
const ExpensesPage = React.lazy(() => import("@/pages/ExpensesPage"));
const RevenuePage = React.lazy(() => import("@/pages/RevenuePage"));
const BudgetPage = React.lazy(() => import("@/pages/BudgetPage"));
const VATReportsPage = React.lazy(() => import("@/pages/VATReportsPage"));
const ProgramDetailPage = React.lazy(() => import("@/pages/ProgramDetailPage"));
const CRMPage = React.lazy(() => import("@/pages/CRMPage"));
const TasksPage = React.lazy(() => import("@/pages/TasksPage"));
const BatchesPage = React.lazy(() => import("@/pages/BatchesPage"));
const BatchTimetablePage = React.lazy(() => import("@/pages/BatchTimetablePage"));
const StudentFinancePage = React.lazy(() => import("@/pages/StudentFinancePage"));
const FlightDispatchPage = React.lazy(() => import("@/pages/FlightDispatchPage"));
const MedicalAlertsPage = React.lazy(() => import("@/pages/MedicalAlertsPage"));
const DocumentExpiryDashboardPage = React.lazy(() => import("@/pages/DocumentExpiryDashboardPage"));
const SecurityDashboardPage = React.lazy(() => import("@/pages/SecurityDashboardPage"));
const VendorsPage = React.lazy(() => import("@/pages/VendorsPage"));
const LeavesPage = React.lazy(() => import("@/pages/LeavesPage"));
const AbsencesPage = React.lazy(() => import("@/pages/AbsencesPage"));
const AcademicLifecyclePage = React.lazy(() => import("@/pages/AcademicLifecyclePage"));
const ContractsPage = React.lazy(() => import("@/pages/ContractsPage"));
const SalaryScalePage = React.lazy(() => import("@/pages/SalaryScalePage"));
const DisciplinaryPage = React.lazy(() => import("@/pages/DisciplinaryPage"));
const ViolationsPage = React.lazy(() => import("@/pages/ViolationsPage"));
const AdminCommsPage = React.lazy(() => import("@/pages/AdminCommsPage"));
const ReportsDashboardPage = React.lazy(() => import("@/pages/ReportsDashboardPage"));
const IncomeStatementPage = React.lazy(() => import("@/pages/IncomeStatementPage"));
const DepartmentsPage = React.lazy(() => import("@/pages/DepartmentsPage"));
const DataCleanupPage = React.lazy(() => import("@/pages/DataCleanupPage"));
const AdminDataToolsPage = React.lazy(() => import("@/pages/AdminDataToolsPage"));
const LegacyPaymentsPage = React.lazy(() => import("@/pages/LegacyPaymentsPage"));
const UserGuidePage = React.lazy(() => import("@/pages/UserGuidePage"));
const AlumniPage = React.lazy(() => import("@/pages/AlumniPage"));
const LicensesPage = React.lazy(() => import("@/pages/LicensesPage"));
const ClientErrorsPage = React.lazy(() => import("@/pages/ClientErrorsPage"));
const PrereqOverridesPage = React.lazy(() => import("@/pages/PrereqOverridesPage"));
const CourseDependencyMapPage = React.lazy(() => import("@/pages/CourseDependencyMapPage"));
const CanaryHistoryPage = React.lazy(() => import("@/pages/CanaryHistoryPage"));
const WorkOrdersPage = React.lazy(() => import("@/pages/WorkOrdersPage"));
const StageChecksPage = React.lazy(() => import("@/pages/StageChecksPage"));
const QuestionBankPage = React.lazy(() => import("@/pages/QuestionBankPage"));
const AuditAnalyzerPage = React.lazy(() => import("@/pages/AuditAnalyzerPage"));
const PreflightPage = React.lazy(() => import("@/pages/PreflightPage"));
const CalendarSyncPage = React.lazy(() => import("@/pages/CalendarSyncPage"));
const SchedulerLogsPage = React.lazy(() => import("@/pages/SchedulerLogsPage"));
const GradeChangeWorkflowPage = React.lazy(() => import("@/pages/GradeChangeWorkflowPage"));
const InvoiceDunningPage = React.lazy(() => import("@/pages/InvoiceDunningPage"));
const FinanceHomePage = React.lazy(() => import("@/pages/FinanceHomePage"));
const LessonDebriefPage = React.lazy(() => import("@/pages/LessonDebriefPage"));
const TrainingRecordsPage = React.lazy(() => import("@/pages/TrainingRecordsPage"));
const AutoSchedulerPage = React.lazy(() => import("@/pages/AutoSchedulerPage"));
const UtilizationPage = React.lazy(() => import("@/pages/UtilizationPage"));
const OperationsBoardPage = React.lazy(() => import("@/pages/OperationsBoardPage"));
const WeatherMinimaSettingsPage = React.lazy(() => import("@/pages/WeatherMinimaSettingsPage"));
const WalkthroughsAdminPage = React.lazy(() => import("@/pages/WalkthroughsAdminPage"));
const PartnerAcademiesPage = React.lazy(() => import("@/pages/PartnerAcademiesPage"));
const PartnerPortalPage = React.lazy(() => import("@/pages/PartnerPortalPage"));
const AuditExportPage = React.lazy(() => import("@/pages/AuditExportPage"));
const AccountingHubPage = React.lazy(() => import("@/pages/AccountingHubPage"));
const ZakatPage = React.lazy(() => import("@/pages/ZakatPage"));
const WHTPage = React.lazy(() => import("@/pages/WHTPage"));
const TaxFilingCalendarPage = React.lazy(() => import("@/pages/TaxFilingCalendarPage"));
const RecurringInvoicesPage = React.lazy(() => import("@/pages/RecurringInvoicesPage"));
const PettyCashPage = React.lazy(() => import("@/pages/PettyCashPage"));
const OpeningBalancesPage = React.lazy(() => import("@/pages/OpeningBalancesPage"));
const VendorBillsPage = React.lazy(() => import("@/pages/VendorBillsPage"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

import { useAuth } from '@/contexts/AuthContext';

function RoleBasedIndex() {
  const { user } = useAuth();
  if (user?.role === 'student') return <StudentPortalPage />;
  if (user?.role === 'instructor') return <InstructorPortalPage />;
  if (user?.role === 'partner_admin' || user?.role === 'partner_instructor') return <PartnerPortalPage />;
  return <AdminDashboard />;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role === 'instructor') return <InstructorPortalPage />;
  if (user?.role === 'student') return <Navigate to="/my-portal" replace />;
  return children;
}

function AppRouter() {
  const { user, loading } = useAuth();
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/student-login" element={<StudentLoginPage />} />
        <Route path="/instructor-login" element={<InstructorLoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify" element={<CertificateVerifyPage />} />
        <Route path="/verify/:certNumber" element={<CertificateVerifyPage />} />
        {/* Public (marketing) calculator for anonymous visitors only — signed-in users get the in-app version below */}
        {!user && !loading && <Route path="/fee-calculator" element={<FeeCalculatorPage isPublic={true} />} />}
        <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<RoleBasedIndex />} />
          <Route path="dashboard" element={<AdminOnly><AdminDashboard /></AdminOnly>} />
          <Route path="fee-calculator" element={gated('fee-calculator', <FeeCalculatorPage />)} />
          <Route path="company/settings" element={gated('company/settings', <CompanySettings />)} />
          <Route path="company/locations" element={gated('company/locations', <CompanyLocations />)} />
          <Route path="company/documents" element={gated('company/documents', <ComplianceDocs />)} />
          <Route path="compliance-reports" element={gated('compliance-reports', <ComplianceReportsPage />)} />
          <Route path="staff" element={gated('staff', <StaffDirectory />)} />
          <Route path="users" element={gated('users', <UserManagement />)} />
          <Route path="users/linkage-health" element={gated('users/linkage-health', <LinkageHealthPage />)} />
          <Route path="audit-logs" element={gated('audit-logs', <AuditLogs />)} />
          <Route path="programs" element={gated('programs', <TrainingProgramsPage />)} />
          <Route path="programs/:programId" element={gated('programs/:programId', <ProgramDetailPage />)} />
          <Route path="students" element={gated('students', <StudentsPage />)} />
          <Route path="students/:studentId" element={gated('students/:studentId', <StudentProfilePage />)} />
          <Route path="student-progress" element={gated('student-progress', <StudentProgressPage />)} />
          <Route path="courses" element={gated('courses', <CoursesPage />)} />
          <Route path="exams" element={gated('exams', <ExamsPage />)} />
          <Route path="certifications" element={gated('certifications', <CertificationsPage />)} />
          <Route path="skill-tests" element={gated('skill-tests', <SkillTestsPage />)} />
          <Route path="classes" element={gated('classes', <ClassesPage />)} />
          <Route path="notifications" element={gated('notifications', <NotificationsPage />)} />
          <Route path="students/:studentId/progress" element={gated('students/:studentId/progress', <StudentProgress />)} />
          <Route path="aircraft" element={gated('aircraft', <AircraftPage />)} />
          <Route path="flight-logs" element={gated('flight-logs', <FlightLogsPage />)} />
          <Route path="maintenance" element={gated('maintenance', <MaintenancePage />)} />
          <Route path="safety" element={gated('safety', <SafetyPage />)} />
          <Route path="weather" element={gated('weather', <WeatherPage />)} />
          <Route path="aviation-reports" element={gated('aviation-reports', <AviationReportsPage />)} />
          <Route path="billing" element={gated('billing', <BillingPage />)} />
          <Route path="payroll" element={gated('payroll', <PayrollPage />)} />
          <Route path="procurement" element={gated('procurement', <ProcurementPage />)} />
          <Route path="reports" element={gated('reports', <ReportsPage />)} />
          <Route path="documents" element={gated('documents', <DocumentsPage />)} />
          <Route path="integrations" element={gated('integrations', <IntegrationSettingsPage />)} />
          <Route path="my-portal" element={gated('my-portal', <StudentPortalPage />)} />
          <Route path="instructor-portal" element={gated('instructor-portal', <InstructorPortalPage />)} />
          <Route path="scheduling" element={gated('scheduling', <SchedulingPage />)} />
          <Route path="analytics" element={gated('analytics', <AnalyticsDashboardPage />)} />
          <Route path="finance-reports" element={gated('finance-reports', <FinanceReportsPage />)} />
          <Route path="payment-plans" element={gated('payment-plans', <PaymentPlansPage />)} />
          <Route path="expenses" element={gated('expenses', <ExpensesPage />)} />
          <Route path="revenue" element={gated('revenue', <RevenuePage />)} />
          <Route path="budgets" element={gated('budgets', <BudgetPage />)} />
          <Route path="vat-reports" element={gated('vat-reports', <VATReportsPage />)} />
          <Route path="crm" element={gated('crm', <CRMPage />)} />
          <Route path="tasks" element={gated('tasks', <TasksPage />)} />
          <Route path="batches" element={gated('batches', <BatchesPage />)} />
          <Route path="batch-timetable" element={gated('batch-timetable', <BatchTimetablePage />)} />
          <Route path="student-finance" element={gated('student-finance', <StudentFinancePage />)} />
          <Route path="flight-dispatch" element={gated('flight-dispatch', <FlightDispatchPage />)} />
          <Route path="medical-alerts" element={gated('medical-alerts', <MedicalAlertsPage />)} />
          <Route path="document-expiry" element={gated('document-expiry', <DocumentExpiryDashboardPage />)} />
          <Route path="security" element={gated('security', <SecurityDashboardPage />)} />
          <Route path="vendors" element={gated('vendors', <VendorsPage />)} />
          <Route path="leaves" element={gated('leaves', <LeavesPage />)} />
          <Route path="absences" element={gated('absences', <AbsencesPage />)} />
          <Route path="academic-lifecycle" element={gated('academic-lifecycle', <AcademicLifecyclePage />)} />
          <Route path="contracts" element={gated('contracts', <ContractsPage />)} />
          <Route path="salary-scale" element={gated('salary-scale', <SalaryScalePage />)} />
          <Route path="disciplinary" element={gated('disciplinary', <DisciplinaryPage />)} />
          <Route path="violations" element={gated('violations', <ViolationsPage />)} />
          <Route path="admin-comms" element={gated('admin-comms', <AdminCommsPage />)} />
          <Route path="reports-dashboard" element={gated('reports-dashboard', <ReportsDashboardPage />)} />
          <Route path="income-statement" element={gated('income-statement', <IncomeStatementPage />)} />
          <Route path="departments" element={gated('departments', <DepartmentsPage />)} />
          <Route path="data-cleanup" element={gated('data-cleanup', <DataCleanupPage />)} />
          <Route path="admin/data-tools" element={gated('admin/data-tools', <AdminDataToolsPage />)} />
          <Route path="finance/legacy-payments" element={<RoleGate roles={['admin','finance']}><LegacyPaymentsPage /></RoleGate>} />
          <Route path="alumni" element={<RoleGate roles={['admin','instructor','finance']}><AlumniPage /></RoleGate>} />
          <Route path="licenses" element={<RoleGate roles={['admin','instructor','finance','student']}><LicensesPage /></RoleGate>} />
          <Route path="client-errors" element={<RoleGate roles={['admin','auditor']}><ClientErrorsPage /></RoleGate>} />
          <Route path="prereq-overrides" element={<RoleGate roles={['admin','auditor']}><PrereqOverridesPage /></RoleGate>} />
          <Route path="course-dependency-map" element={<RoleGate roles={['admin','auditor','instructor']}><CourseDependencyMapPage /></RoleGate>} />
          <Route path="canary-history" element={<RoleGate roles={['admin']}><CanaryHistoryPage /></RoleGate>} />
          <Route path="work-orders" element={<RoleGate roles={['admin','instructor','auditor']}><WorkOrdersPage /></RoleGate>} />
          <Route path="stage-checks" element={<RoleGate roles={['admin','instructor','auditor']}><StageChecksPage /></RoleGate>} />
          <Route path="question-bank" element={<RoleGate roles={['admin','instructor']}><QuestionBankPage /></RoleGate>} />
          <Route path="audit-analyzer" element={<RoleGate roles={['admin','auditor']}><AuditAnalyzerPage /></RoleGate>} />
          <Route path="preflight" element={<RoleGate roles={['admin','instructor']}><PreflightPage /></RoleGate>} />
          <Route path="calendar-sync" element={gated('calendar-sync', <CalendarSyncPage />)} />
          <Route path="scheduler-logs" element={<RoleGate roles={['admin','auditor']}><SchedulerLogsPage /></RoleGate>} />
          <Route path="grade-change-requests" element={<RoleGate roles={['admin','instructor','chief_instructor','auditor']}><GradeChangeWorkflowPage /></RoleGate>} />
          <Route path="invoice-dunning" element={<RoleGate roles={['admin','finance','auditor']}><InvoiceDunningPage /></RoleGate>} />
          <Route path="finance-home" element={<RoleGate roles={['admin','finance']}><FinanceHomePage /></RoleGate>} />
          <Route path="debrief" element={<RoleGate roles={['admin','instructor']}><LessonDebriefPage /></RoleGate>} />
          <Route path="training-records" element={<RoleGate roles={['admin','instructor','student','auditor']}><TrainingRecordsPage /></RoleGate>} />
          <Route path="auto-scheduler" element={<RoleGate roles={['admin','instructor']}><AutoSchedulerPage /></RoleGate>} />
          <Route path="utilization" element={<RoleGate roles={['admin','instructor','auditor']}><UtilizationPage /></RoleGate>} />
          <Route path="operations-board" element={<RoleGate roles={['admin','instructor','auditor']}><OperationsBoardPage /></RoleGate>} />
          <Route path="weather-minima" element={<RoleGate roles={['admin','instructor','auditor']}><WeatherMinimaSettingsPage /></RoleGate>} />
          <Route path="walkthroughs-cms" element={<RoleGate roles={['admin']}><WalkthroughsAdminPage /></RoleGate>} />
          <Route path="partner-academies" element={<RoleGate roles={['admin','finance','auditor']}><PartnerAcademiesPage /></RoleGate>} />
          <Route path="partner-portal" element={<RoleGate roles={['partner_admin','partner_instructor','admin']}><PartnerPortalPage /></RoleGate>} />
          <Route path="audit-export" element={<RoleGate roles={['admin','auditor']}><AuditExportPage /></RoleGate>} />
          <Route path="accounting" element={<RoleGate roles={['admin','finance','auditor']}><AccountingHubPage /></RoleGate>} />
          <Route path="zakat" element={<RoleGate roles={['admin','finance','auditor']}><ZakatPage /></RoleGate>} />
          <Route path="wht" element={<RoleGate roles={['admin','finance','auditor']}><WHTPage /></RoleGate>} />
          <Route path="tax-calendar" element={<RoleGate roles={['admin','finance','auditor']}><TaxFilingCalendarPage /></RoleGate>} />
          <Route path="recurring-invoices" element={<RoleGate roles={['admin','finance']}><RecurringInvoicesPage /></RoleGate>} />
          <Route path="petty-cash" element={<RoleGate roles={['admin','finance']}><PettyCashPage /></RoleGate>} />
          <Route path="opening-balances" element={<RoleGate roles={['admin']}><OpeningBalancesPage /></RoleGate>} />
          <Route path="vendor-bills" element={<RoleGate roles={['admin','finance']}><VendorBillsPage /></RoleGate>} />
          <Route path="user-guide" element={gated('user-guide', <UserGuidePage />)} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
